import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import loginIMG from '../../../assets/loginIMG2.png';
import { captchaService, passwordService, validatePasswordStrength } from '../../../services/authService';
import { loginFormError, LOGIN_ID_MAX_LENGTH, CAPTCHA_LENGTH } from '../../../shared/validation/authInput';

type Step = 'identify' | 'verify' | 'reset';

const RESEND_COOLDOWN_SECONDS = 60;

function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('identify');
  const [loginId, setLoginId] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [maskedEmail, setMaskedEmail] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const loadCaptcha = useCallback(async () => {
    try {
      const data = await captchaService.createCaptcha();
      setCaptchaId(data.captchaId);
      setCaptchaSvg(data.captchaSvg);
      setCaptcha('');
    } catch {
      setError('Failed to load captcha. Please refresh the page.');
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  // Resend cooldown — stops users hammering the OTP endpoint and hitting the
  // server-side rate limit (3 codes per 10 minutes).
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleRequestOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setNotice('');

    // Same rules as the login form and the server (Improper Input Validation).
    const inputError = loginFormError(loginId, captcha);
    if (inputError) {
      setError(inputError);
      return;
    }

    setLoading(true);

    try {
      const response = await passwordService.requestResetOtp({
        username: loginId.trim(),
        captchaId,
        captchaValue: captcha,
      });

      // No notice here — the step hint already says a code went to this address.
      // Repeating it puts the same sentence on screen twice.
      setMaskedEmail(response.email || '');
      setStep('verify');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      // The 404 message already names the Login ID and its case-sensitivity —
      // show what the server said rather than appending to it here.
      setError(err.message || 'Could not send the verification code');
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Every send needs a freshly solved captcha — the answer only ever lives on
   * the server, so the user has to go back a step and solve a new one. Sending
   * a second code without that check would make the captcha on step 1 pointless.
   */
  const handleResend = async () => {
    if (cooldown > 0) return;
    setError('');
    setOtp('');
    await loadCaptcha();
    setStep('identify');
    setNotice('Solve the captcha to send a new code.');
  };

  const handleVerifyOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);

    try {
      await passwordService.verifyResetOtp({ username: loginId.trim(), otp: otp.trim() });
      // Confirmation only — "choose a new password" is already the step hint.
      setNotice('Code verified.');
      setStep('reset');
    } catch (err: any) {
      setError(err.message || 'Could not verify the code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setNotice('');

    const policyError = validatePasswordStrength(newPassword);
    if (policyError) {
      setError(policyError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await passwordService.resetPassword(newPassword);
      navigate('/login', {
        replace: true,
        state: { passwordReset: true },
      });
    } catch (err: any) {
      setError(err.message || 'Could not update the password');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full border-b border-gray-300 focus:border-purple-600 outline-none py-2 px-1 mb-1 text-sm md:text-base bg-transparent disabled:bg-gray-100';

  const stepTitle = {
    identify: 'Forgot Password',
    verify: 'Enter Verification Code',
    reset: 'Set New Password',
  }[step];

  const stepHint = {
    identify: 'Enter your Login ID. We will email a code to the address registered on your account.',
    verify: maskedEmail
      ? `Enter the 6-digit code sent to ${maskedEmail}.`
      : 'Enter the 6-digit code sent to your registered email.',
    reset: 'Choose a new password. You will be logged out of all devices.',
  }[step];

  return (
    <div className="w-full min-h-full flex items-center justify-center px-4 py-10">
      <div className="relative rounded-2xl shadow-2xl overflow-hidden w-full max-w-[1000px] lg:min-h-[487px] bg-white">
        {/* The artwork carries its own S-curve and its own white right-hand
            third, so it is laid over the card at natural size and the form sits
            on that white area — same trick as LoginPage. Absolute rather than a
            negative margin so the form's left offset is stated outright and
            cannot drift into the purple. */}
        <img
          src={loginIMG}
          alt=""
          aria-hidden="true"
          className="hidden lg:block absolute left-0 top-0 h-full w-[630px] object-cover pointer-events-none select-none"
        />

        {/* Form column. lg:pl clears the artwork's widest point — the curve
            sweeps out to x=627 around y=48-72, which is exactly where this
            form's heading sits, so anything less overlaps the purple. */}
        <div className="relative flex flex-col justify-center py-8 px-6 sm:px-10 lg:pl-[646px] lg:pr-12 lg:min-h-[487px]">
          <div className="w-full max-w-sm mx-auto lg:mx-0">
            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-3">
              {(['identify', 'verify', 'reset'] as Step[]).map((s, i) => (
                <div
                  key={s}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    (['identify', 'verify', 'reset'] as Step[]).indexOf(step) >= i ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>

            <div className="text-lg font-semibold text-purple-700 mb-1">{stepTitle}</div>
            <p className="text-xs text-gray-500 mb-3">{stepHint}</p>

            {error && (
              <div
                role="alert"
                className="bg-red-50 border-l-2 border-red-400 text-red-700 px-3 py-1.5 rounded-r text-xs leading-snug mb-2"
              >
                {error}
              </div>
            )}
            {notice && !error && (
              <div className="bg-green-50 border-l-2 border-green-400 text-green-700 px-3 py-1.5 rounded-r text-xs leading-snug mb-2">
                {notice}
              </div>
            )}

            {/* ---------- Step 1: Login ID + captcha ---------- */}
            {step === 'identify' && (
              <form onSubmit={handleRequestOtp} autoComplete="off">
                <label htmlFor="loginId" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Login ID <span className="font-normal text-gray-500">(case-sensitive)</span>
                </label>
                <input
                  type="text"
                  id="loginId"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  placeholder="Enter Login ID"
                  required
                  maxLength={LOGIN_ID_MAX_LENGTH}
                  autoComplete="off"
                  // Login IDs are case-sensitive on the server — stop a mobile
                  // keyboard from capitalising the first letter.
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  disabled={loading}
                  className={inputClass}
                />

                <label htmlFor="captcha" className="block text-xs md:text-sm font-medium text-gray-700 mb-1 mt-3">
                  Captcha
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    id="captcha"
                    value={captcha}
                    onChange={(e) => setCaptcha(e.target.value)}
                    placeholder="Enter Captcha"
                    required
                    maxLength={CAPTCHA_LENGTH}
                    autoComplete="off"
                    disabled={loading}
                    className={`flex-1 ${inputClass}`}
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                  />
                  {captchaSvg ? (
                    <div
                      className="h-12 w-32 border border-gray-300 rounded flex items-center justify-center bg-white"
                      dangerouslySetInnerHTML={{ __html: captchaSvg }}
                    />
                  ) : (
                    <div className="h-10 w-24 border border-gray-300 rounded flex items-center justify-center bg-gray-100">
                      <span className="text-xs text-gray-400">Loading...</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={loadCaptcha}
                    disabled={loading}
                    title="Refresh Captcha"
                    className="h-10 w-10 border border-gray-300 rounded flex items-center justify-center hover:bg-gray-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold shadow-md hover:from-purple-700 hover:to-purple-800 transition-colors mt-6 h-[46px] disabled:bg-purple-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send Code'}
                </button>
              </form>
            )}

            {/* ---------- Step 2: OTP ---------- */}
            {step === 'verify' && (
              <form onSubmit={handleVerifyOtp} autoComplete="off">
                <label htmlFor="otp" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  6-digit code
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  required
                  autoComplete="one-time-code"
                  disabled={loading}
                  className={`${inputClass} tracking-[0.5em] text-lg font-semibold`}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
               />

                <div className="flex items-center justify-between mt-3">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={loading || cooldown > 0}
                    className="text-xs text-purple-700 hover:text-purple-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('identify');
                      setOtp('');
                      setError('');
                      setNotice('');
                      loadCaptcha();
                    }}
                    disabled={loading}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Change Login ID
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold shadow-md hover:from-purple-700 hover:to-purple-800 transition-colors mt-6 h-[46px] disabled:bg-purple-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
              </form>
            )}

            {/* ---------- Step 3: new password ---------- */}
            {step === 'reset' && (
              <form onSubmit={handleResetPassword} autoComplete="off">
                <label htmlFor="newPassword" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    autoComplete="new-password"
                    disabled={loading}
                    className={`${inputClass} pr-10`}
                     onPaste={(e) => e.preventDefault()}
  onCopy={(e) => e.preventDefault()}
  onCut={(e) => e.preventDefault()}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.58 10.58A2 2 0 0013.42 13.42M9.88 5.09A9.77 9.77 0 0112 5c5 0 9 5 9 7a11.4 11.4 0 01-2.08 3.11M6.1 6.1C3.91 7.6 2.4 9.7 2 12c0 2 4 7 10 7a9.74 9.74 0 004.32-.99" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>

                <label htmlFor="confirmPassword" className="block text-xs md:text-sm font-medium text-gray-700 mb-1 mt-2">
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                  autoComplete="new-password"
                  disabled={loading}
                  className={inputClass}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                />

                {/* One compact line, not a bullet list — three <li>s made this
                    step ~65px taller than the others and the card visibly jumped
                    size between steps. */}
                <p className="text-[11px] text-gray-500 mt-3 leading-snug">
                  At least 8 characters, with a capital, a small letter, a number and a special character.
                </p>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold shadow-md hover:from-purple-700 hover:to-purple-800 transition-colors mt-4 h-[46px] disabled:bg-purple-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            )}

            <div className="text-center mt-3">
              <Link to="/login" className="text-xs text-gray-500 hover:text-purple-700">
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
